import styled, { css } from "styled-components";
import {
  COMPACT_INPUT_HEIGHT_PX,
  COMPACT_INPUT_WIDTH_PX,
  Container as TimePickerContainer,
  FieldWrapper as TimePickerFieldWrapper,
  Input as TimePickerInput,
} from "components/designV2/timePicker/timePicker.styles";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import {
  DetailCheckboxCell,
  DetailRow,
  StatusChip,
  StatusColumn,
} from "./personSummaryRow.styled";
import {
  DETAILED_ROW_COLUMN_GAP,
  getDetailedGridColumns,
} from "./personRow.layout.logic";

const valueTypography = css`
  ${TypographyV2.b1};
  line-height: 24px;
`;

export const DetailedRow = styled(DetailRow).attrs({
  className: "time-report-detailed-row",
})<{ readonly $hasDeviation: boolean }>`
  grid-template-columns: ${(props) =>
    getDetailedGridColumns(props.$hasDeviation)};
  min-width: var(--time-report-detailed-min-width, 1612px);
  column-gap: ${DETAILED_ROW_COLUMN_GAP};
  align-items: start;
  cursor: default;

  &:hover {
    background: ${ColorV2.Blue7};
  }

  > :last-child {
    height: ${COMPACT_INPUT_HEIGHT_PX}px;
    justify-self: end;
  }

  ${TimePickerContainer} {
    width: var(--time-report-picker-width, ${COMPACT_INPUT_WIDTH_PX}px);
  }

  ${TimePickerFieldWrapper} {
    padding: var(--time-report-picker-padding, 0 16px);
  }
`;

export const DetailedIdentityColumn = styled.div`
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr);
  column-gap: 32px;
  align-items: start;
  align-self: stretch;
  min-width: 0;

  ${DetailCheckboxCell} {
    align-self: stretch;
  }
`;

export const DateButton = styled.button`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  > span:first-child {
    ${valueTypography};
    display: flex;
    align-items: center;
    min-height: ${COMPACT_INPUT_HEIGHT_PX}px;
  }
  &:focus-visible {
    outline: 2px solid ${ColorV2.Blue1};
    outline-offset: 4px;
  }
`;

export const DetailColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

export const ColumnLabel = styled.span`
  ${TypographyV2.s1};
  color: ${ColorV2.Blue4};
`;

export const ReportedTimeLabel = styled(ColumnLabel)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  white-space: nowrap;
`;

export const ColumnValue = styled.span`
  ${valueTypography};
  color: ${ColorV2.Blue1};
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  min-height: ${COMPACT_INPUT_HEIGHT_PX}px;
`;

export const InputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: ${COMPACT_INPUT_HEIGHT_PX}px;

  ${TimePickerInput} {
    ${valueTypography};
    padding: 0;
  }
`;

export const DetailedStatusColumn = styled(StatusColumn)`
  grid-column: 7 / -2;
  flex-direction: row;
  align-items: flex-start;
  max-width: 100%;
  gap: 8px;
`;

export const DetailedStatusContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 0;
  gap: 4px;
  ${StatusChip} {
    max-width: 100%;
    white-space: normal;
  }
  > div {
    flex-wrap: wrap;
    min-height: ${COMPACT_INPUT_HEIGHT_PX}px;
    align-items: center;
  }
`;

export const RowFeedback = styled.span<{ readonly $hasError?: boolean }>`
  ${TypographyV2.s1};
  color: ${(props) => (props.$hasError ? ColorV2.NegativeRed : ColorV2.Blue4)};
`;

export const CommentCell = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  min-height: ${COMPACT_INPUT_HEIGHT_PX}px;

  > div {
    align-self: center;
  }
`;

export const RetryButton = styled.button`
  ${TypographyV2.s1};
  background: transparent;
  border: none;
  color: ${ColorV2.Blue1};
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
`;
